import { Controller } from "@hotwired/stimulus"

// Fills the HubSpot CRM writer form's property select with the properties a score can be
// written to on the chosen object. The url reads them through the HubSpot connection's source,
// and is empty when the connection has none. The server decides which properties qualify; this
// only lists what it returns.
export default class extends Controller {
  static targets = ["object", "property", "error"]
  static values = { url: String }

  connect() {
    this.load()
  }

  // Called with an event when the operator changes the object. The selected property belongs to
  // the previous object then, and keeping it would save a property name the new object may lack.
  async load(event) {
    const object = this.objectTarget.value
    const selected = event ? "" : this.propertyTarget.value

    this.hideError()

    if (!this.urlValue) {
      this.setOptions([], "The HubSpot connection has no source to read from", selected)
      return
    }

    if (!object) {
      this.setOptions([], "Select an object first", selected)
      return
    }

    this.setOptions([], "Loading properties...", selected)

    const url = `${this.urlValue}?object=${encodeURIComponent(object)}`

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const body = await response.json()

      if (!response.ok) {
        this.setOptions([], "Properties couldn't be loaded", selected)
        this.showError(body.error || "Properties couldn't be loaded from HubSpot")
        return
      }

      const placeholder = body.properties.length > 0 ? "Select a property..." : "No Number properties on this object"
      this.setOptions(body.properties, placeholder, selected)
    } catch (error) {
      this.setOptions([], "Properties couldn't be loaded", selected)
      this.showError(`Properties couldn't be loaded from HubSpot: ${error.message}`)
    }
  }

  // Keeps the saved value selectable even when the list can't load, so saving the form
  // never drops it silently.
  //
  // The select is wrapped by SlimSelect (search-select controller, which exposes the instance as
  // `element.slimselect`). Its options go through SlimSelect's own API: when it picks up a DOM
  // change it keeps its previous selection by its internal option ids, so replacing the options
  // underneath it leaves the old "Loading properties..." choice on display.
  setOptions(properties, placeholder, selected) {
    const options = [{ text: placeholder, value: "" }]
    properties.forEach(({ name, label }) => options.push({ text: `${label} (${name})`, value: name }))

    if (selected && !properties.some(({ name }) => name === selected)) {
      options.push({ text: selected, value: selected })
    }

    const slimselect = this.propertyTarget.slimselect
    if (slimselect) {
      slimselect.setData(options)
      slimselect.setSelected(selected || "")
    } else {
      // Reached on the first load: connect() runs before the search-select controller on the
      // select itself has wrapped it.
      this.propertyTarget.replaceChildren(...options.map(({ text, value }) => new Option(text, value)))
      this.propertyTarget.value = selected || ""
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
